import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, ButtonAPI, DeckAPI, FilterAPI, MapperAPI, ModalButton, ModalProps, ModalService, PanelAPI, SegmentedControlAPI, Tag, Tooltip, TooltipTrigger } from '@philips-internal/oswald-ui-components';
import { ButtonTheme, ModalButtonType } from '@philips-internal/oswald-ui-constants/lib';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeClear, OnBeforeSubmit, OnChange, OnClick, OnDynamicContentRender, OnMount, OnSelectGroup, OnUnmount, OnUnselectGroup } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { AbortableSubmitEvent, ChangeEvent, ClickEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import moment, { Moment as MomentType } from 'moment';
import React from 'react';
import { FilterSuggestionOrders } from '../../app/types';
import { FragmentNames, SugestionListAliases } from '../../codegen';
import TokenConstants from '../../constants/TokenConstants';
import { filterSuggestionOrders } from '../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { EstablishmenSuggestions, ScheduleItem, SuggestionGroups, SuggestionItems, SuggestionPeriodItem } from '../../interfaces/schedules-suggestions';
import AppointmentContext from '../../utils/appointment-context';
import { AppointmentUtils } from '../../utils/appointment-utils';
import { cast } from '../../utils/cast-helpers';
import { JWTTokenUtils } from '../../utils/jwt-token-utils';
import { showToastNotificationError, showToastNotificationInformation } from '../../utils/toast-notification-utils';
import BaseController from '../BaseController';
import './suggestion-modal.css';
import PriceService, { Price } from '../pseudonym-appointments/pseudonym-service/price.service';

interface Establishment {
  description: string;
  id: number;
}

interface FilterSuggestionRecord {
  establishment?: Establishment;
  onlyTeleconsultation?: boolean;
  ordenation?: FilterSuggestionOrders;
}

interface State {
  initialValues: {
    suggestionList: EstablishmenSuggestions[];
  },
  isShowPrice: boolean;
}
interface DesiredAppointmentDate {
  date: MomentType;
  time: string;
}
@Controller({ component: 'suggestion-layout', fragment: 'sugestion-list' })
class SuggestionModalController extends BaseController {

  private suggestionList: EstablishmenSuggestions[] = [];

  private hasSeleceted = false;

  private hasGroupingWithRequest = false;

  private lastAPI?: DeckAPI;

  private selectedList: ScheduleItem[] = [];

  private wantToRequest = false;

  private suggestionGroup!: SuggestionGroups;

  private numberTimesSchedulingDay!: number;

  private suggestionItemSelected: SuggestionItems[] = [];

  private decksSuggestionItems: DeckAPI[] = [];

  private mapper0CurrentDataToBeUsed: Record<string, Value>[] = [];

  private segmentedControlCurrentDataToBeUsed: Record<string, Value>[] = [];

  private segmentedControlCurrentValueToBeUsed: Record<string, Value>[] = [];

  @Inject('segmented-control-week-days')
    segmentedControlWeekDays!: SegmentedControlAPI;

  @Inject('button-confirm')
    buttonConfirm!: ButtonAPI;

  @Inject(SugestionListAliases.MAPPER_SUGGESTIONS_SUGGESTION_LIST)
    mapper!: MapperAPI;

  @Inject('panel-suggestions')
    panelSuggestions!: PanelAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject('panel-suggestions-content')
    panelSuggestionsContentAPI!: PanelAPI;

  @Inject('panel-load-content')
    panelLoadContentAPI!: PanelAPI;

  @Inject('PriceService')
    priceService!: PriceService;

  @OnMount({ target: SugestionListAliases.PANEL_SUGGESTIONS_CONTENT_SUGGESTION_LIST})
  onMount(event: MountEvent): void {
    if (event) {
      this.suggestionGroup = this.context.get('data') as unknown as SuggestionGroups;
      const isShowPrice = this.suggestionGroup.isShowPrice;
      this.suggestionList = this.suggestionGroup.establishmenSuggestionsGroups;
      this.numberTimesSchedulingDay = this.suggestionGroup.numberTimesSchedulingDay;

      const state: State = {
        initialValues: {
          suggestionList: [...this.suggestionList],
        },
        isShowPrice,
      }
      this.context.set({ state });

      this.processWeekdays();
    }
  }

  /**
   * Temporary adjustment. Removes "Load More" button when there is no Fetch.
   * Will be fixed by tec in the next version.
   */
  @OnMount({target: ['deck-suggestion-items', 'deck-suggestion-groups']})
  handleMountDeckSuggestion(event: MountEvent): void {
    const deck = event.target as DeckAPI;
    if(!deck.isDone()) {
      deck.setDone(true);
    }

    if(deck.getAlias() === 'deck-suggestion-items') {
      const suggestionItems = deck.getData() as unknown as SuggestionItems[];
      const identifier = suggestionItems[0]?.suggestionItems[0]?.suggestionGroupId;
      if(identifier) {
        this.decksSuggestionItems[identifier] = deck;
      }
    }
  }

  @OnUnmount({target: 'deck-suggestion-items'})
  handleUnMountDeckSuggestion(event: MountEvent): void {
    const deck = event.target as DeckAPI;
    if(deck?.selectGroup?.length > 0) {
      this.lastAPI = undefined;
      this.hasSeleceted = false;
      this.suggestionItemSelected = [];
      this.buttonConfirm.setEnabled(false);
    }
  }

  @OnChange({ target: 'segmented-control-week-days' })
  showSuggestionByDate(event: ChangeEvent<Value>): void {
    if (event && event.value) {
      this.hasSeleceted = false;
      const state = this.context.get('state') as State;
      const segmentedDateItem = event.value as SuggestionPeriodItem[];
      if (!segmentedDateItem.length) {
        return;
      }

      this.panelSuggestions.setVisible(true);
      setTimeout(() => {
        const suggestionsGroups = this.suggestionList
          .filter(suggestionGroup => {
            return suggestionGroup.value === segmentedDateItem[0].date;
          })
          .slice(0, this.numberTimesSchedulingDay);

        suggestionsGroups.forEach((suggestionsGroup, index) => {
          suggestionsGroup.suggestions.forEach(suggestionItems => {
            suggestionItems.suggestionItems.forEach(suggestion => {
              suggestion.suggestionGroupId = index + 1;
              suggestion.dsEstablishment = suggestionsGroup.dsEstablishment;
              suggestion.cdEstabelecimento = suggestionsGroup.cdEstabelecimento;
              suggestion.isShowPrice = state.isShowPrice;
            })
          });
        });

        setTimeout(() => {
          this.mapper.setData(cast(suggestionsGroups));
          this.segmentedControlCurrentValueToBeUsed = cast(segmentedDateItem);
        });
      });
    }
  }

  @OnDynamicContentRender({ target: 'deck-suggestion-items' })
  onDeckGroupRender(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const state = this.context.get('state') as State;
      const suggestion = event.data as SuggestionItems[];
      const schedulePrice = { currencySymbol: '', total: 0 } as Price;
      const copagoPrice = { currencySymbol: '', total: 0 } as Price;
      const canSelected = this.priceService.calculatePrice(suggestion, schedulePrice, copagoPrice);
      const isShowTotal = AppointmentUtils.isShowTotal(suggestion[0]?.suggestionItems || []);
      const totalLabel = this.priceService.getValueToDisplay(schedulePrice, this.priceService.Label.SCHEDULE_TOTAL);
      const copagoLabel = this.priceService.getValueToDisplay(copagoPrice, this.priceService.Label.COPAGO);
      const viewOnlyClass = `${!canSelected ? 'deck-title-suggestion-item--no-select' : 'deck-title-suggestion-item' }`;
      const oswaldDeckHeaderClass = 'c-deck__group-header-default-content';
      const oswaldDeckTitleClass = 'c-deck__group-header-title';

      const renderSuggestionDate: () => JSX.Element = () => {
        if (!canSelected) {
          return (
            <div className={`${oswaldDeckHeaderClass} ${viewOnlyClass}`}>
              <div className={oswaldDeckTitleClass}>
                {MessageService.translate('tws.appointment-suggestion.suggestion')} {suggestion[0].suggestionId}
              </div>
              <Tooltip
                triggers={[TooltipTrigger.HOVER]}
                value={MessageService.translate('patient.tooltip.visualisation')}
                multiline={true}
              >
                <div className='tooltip-custom__target'>
                  <Tag
                    value={MessageService.translate('patient.view-only')}
                    formatters={{}}
                  />
                </div>
              </Tooltip>
              { state.isShowPrice && isShowTotal &&
                <div className='price'>
                  {totalLabel}
                </div>
              }
              { copagoLabel != '' &&
                <div className='price'>
                  {copagoLabel}
                </div>
              }
            </div>
          )
        }

        const currentDeckAPI = event.target as DeckAPI;
        return (
          <>
            <div className="wrapper-full-click" onClick={currentDeckAPI.onClick.fire}></div>
            <div className={`${oswaldDeckHeaderClass} deck-title-suggestion-item`}>
              <div className={oswaldDeckTitleClass}>
                {MessageService.translate('tws.appointment-suggestion.suggestion')} {suggestion[0].suggestionId}
              </div>
              { state.isShowPrice && isShowTotal &&
              <div className='price'>
                {totalLabel}
              </div> }
              { copagoLabel != '' &&
                <div className='price'>
                  {copagoLabel}
                </div>
              }
            </div>
          </>
        );
      }
      render(renderSuggestionDate);
    }
  }

  @OnSelectGroup({ target: 'deck-suggestion-items' })
  onSelectSuggestionItem(event: SelectItemEvent<Record<string, Value>[]>): void{
    if (event) {
      const api = event.target as unknown as DeckAPI;
      const selecetedItem = event.item as unknown as SuggestionItems[];

      if (this.checkCanSelectByType(selecetedItem)) {
        const { suggestionId: identifierGroup, suggestionGroupId: deckIndex } = selecetedItem[0].suggestionItems[0];
        const typeScheduleHybrid = 'H';

        const suggestionsHybrid = selecetedItem[0].suggestionItems
          .filter(suggestion => suggestion.teleconsultationType === typeScheduleHybrid);

        if (suggestionsHybrid?.length) {
          this.openDialogHasConfirmTypeConsultation()
        }

        if (this.lastAPI && this.hasSeleceted) {
          this.lastAPI.unselectAll();
          api.selectGroup(String(identifierGroup));
        } else {
          this.hasSeleceted = true;
        }

        this.lastAPI = this.decksSuggestionItems[deckIndex];

        if (this.hasSeleceted && selecetedItem) {
          this.suggestionItemSelected = selecetedItem;
          this.buttonConfirm.setEnabled(true);
        } else {
          this.buttonConfirm.setEnabled(false);
        }
      } else {
        this.hasSeleceted = false;
        api.unselectAll();
        if(this.lastAPI && this.suggestionItemSelected && this.suggestionItemSelected.length > 0) {
          const lastDeckSelected = this.suggestionItemSelected[0].suggestionId;
          this.lastAPI.selectGroup(String(lastDeckSelected || ''));
          this.hasSeleceted = !!lastDeckSelected;
        }
        return;
      }
    }
  }

  @OnUnselectGroup({ target: 'deck-suggestion-items' })
  onUnselectGroup(event: SelectItemEvent<Record<string, Value>[]>): void {
    if (event) {
      this.hasSeleceted = false;
      if (!this.hasSeleceted) {
        this.lastAPI = undefined;
        this.buttonConfirm.setEnabled(false);
      }
    }
  }

  @OnClick({ target: 'button-confirm' })
  confirmSuggestion(event: ClickEvent): void {
    if (!this.hasSeleceted || !this.selectedList || !event) {
      return;
    }

    const allowSelectDesiredDate = this.context.get('allowSelectDesiredDate') as boolean;
    const desiredDateCustomMessage = this.context.get('desiredDateCustomMessage') as string;

    allowSelectDesiredDate ? this.openAskForDesiredDateDialog(event, desiredDateCustomMessage) : this.handleConfirmSuggestion(event);
  }

  private openAskForDesiredDateDialog(event: ClickEvent, customMessage: string): void {
    ModalService.openDialog({
      closeable: false,
      dialogDescription: customMessage || 'patient.appoitment.correct.date',
      dockButtons: [
        {
          action: () => this.openModalToSelectDesiredAppointmentDate(event),
          title: MessageService.translate('patient.modal.button-cancel'),
          type: ModalButtonType.CANCEL
        },
        {
          action: (): void => {
            this.handleConfirmSuggestion(event);
            this.openToastDesiredDate();
          },
          title: MessageService.translate('patient.moda.button-yes'),
          type: ModalButtonType.SUCCESS
        }
      ],
      title: 'patient.appointment.confirm.title'
    });
  }

  private openModalToSelectDesiredAppointmentDate(clickEvent: ClickEvent): void {
    const context = this.context.get('data') as unknown as Record<string, Value[]>;
    const suggestionFirstItem = this.suggestionItemSelected[0].suggestionItems[0];

    const modalProps: ModalProps = {
      className: FragmentNames.APPOINTMENT_CREATION_CHOOSE_ANOTHER_DATE,
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { suggestionFirstItem },
      focusOnOpen: false,
      fragment: FragmentNames.APPOINTMENT_CREATION_CHOOSE_ANOTHER_DATE,
      id: FragmentNames.APPOINTMENT_CREATION_CHOOSE_ANOTHER_DATE,
      noPadding: true,
      onSubmit: evt => this.handleSubmitDesiredDate(evt, clickEvent, context),
      title: '',
      uuid: '',
    }

    isMobile() ? ModalService.openFullscreen(modalProps): ModalService.openDialog(modalProps);
  }

  handleSubmitDesiredDate(submitEvent: SubmitEvent, clickEvent: ClickEvent, data: Record<string, Value[]>): void {
    const desiredAppointmentDate = submitEvent.record as DesiredAppointmentDate;
    const desiredDate = this.getDesiredDate(desiredAppointmentDate.date, desiredAppointmentDate.time.replaceAll('Z', ':00Z'));
    const selecetedItems: SuggestionItems[] = this.suggestionItemSelected;

    if (this.hasSeleceted && selecetedItems.length > 0) {
      selecetedItems.forEach(item => {
        item.suggestionItems.forEach(suggestionItem => {

          const body = {
            desiredDate: desiredDate,
            scheduleType: suggestionItem.appointmentType,
            sequenceIntegratedSchedule: data.nrSeqAgeint,
            sequenceNumber: suggestionItem.nrSeqAgeintItem
          }

          HttpService.post(APPLICATION_ENDPOINTS.DESIRED_DATE, {}, { body });
        });
      })
    }

    this.handleConfirmSuggestion(clickEvent);
    this.openToastDesiredDate();
  }

  openToastDesiredDate(): void {
    showToastNotificationInformation(MessageService.translate('patient.desired.date.acknowledgment'));
  }

  handleConfirmSuggestion(event: ClickEvent): void {
    if (event) {
      const selecetedItems: SuggestionItems[] = this.suggestionItemSelected;
      if (this.hasSeleceted && selecetedItems[0]) {
        const suggestionItems = selecetedItems[0].suggestionItems;
        if(suggestionItems.length > 1) {
          const suggestionItem = suggestionItems.find(item => item.availableType === 'R');
          this.hasGroupingWithRequest = Boolean(suggestionItem);
        }

        this.checkNecessaryConfirmationItems(selecetedItems)
          .then(() => {
            this.submittableFragmentContext.submit(new SubmitEvent({ record: this.selectedList }));
          })
          .catch(this.openDialogHasAppointment);
      }
    }
  }

  openDialogHasConfirmTypeConsultation(): void {

    const typeScheduleHybrid = 'H';

    const buttons: ModalButton[] = [
      {
        action: () => {},
        theme: ButtonTheme.SECONDARY,
        title: 'patient.cancel',
        type: ModalButtonType.CANCEL,
      },
      {
        action: () => {},
        title: 'patient.burron.confirm',
        type: ModalButtonType.SUCCESS,
      }
    ];

    ModalService.openDialog({
      closeable: true,
      dockButtons: buttons,
      fragment: FragmentNames.SUGGESTION_TYPE_CONSULTATION,
      onSubmit: ( event ) => {
        this.suggestionItemSelected[0].suggestionItems.forEach(suggestion => {
          if(suggestion.teleconsultationType === typeScheduleHybrid) {
            suggestion.suggestionTypeConsultation = event.record.suggestionTypeConsultation.value;
          }
        });
      },
      title: 'text.choose-an-option-below',
    });
  }

  openDialogHasAppointment(text: string): void {
    const buttons: ModalButton[] = [
      {
        action: () => { return },
        title: 'patient.confirm',
        type: ModalButtonType.SUCCESS,
      },
    ];
    ModalService.openDialog({
      closeable: true,
      dialogDescription: text,
      dockButtons: buttons,
      title: 'patient.process.aborted',
    });
  }

  checkCanSelectByType(selecetedItems: SuggestionItems[]): boolean {
    let canSelect = true;
    selecetedItems[0].suggestionItems.forEach(suggestion => {
      if(suggestion.availableType === 'V') {
        canSelect = false;
      }
    });
    return canSelect;
  }

  async checkNecessaryConfirmationItems(selecetedItems: SuggestionItems[]): Promise<void> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<void>(async (resolve, reject) => {
      const suggestionItems = selecetedItems[0].suggestionItems;
      for (const suggestion of suggestionItems) {
        if (suggestion.availableType === 'R') {
          await this.confirmWantToRequest().then(async () => {
            await this.availableTypeModal(suggestion).catch(() => {
              reject();
            });
          });
        } else {
          this.selectedList.push(suggestion);
        }
      }
      if(!this.hasGroupingWithRequest) {
        const selectedSchedules = this.selectedList.filter(item => item.availableType !== 'R');
        AppointmentContext.callUpdateAppointmentCard(this.suggestionGroup.groupId, selectedSchedules);
      }
      resolve();
    });
  }

  confirmWantToRequest(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.wantToRequest) {
        const buttons: ModalButton[] = [
          {
            action: () => {
              this.wantToRequest = false;
              reject();
            },
            title: 'patient.cancel',
            type: ModalButtonType.CANCEL,
          },
          {
            action: () => {
              this.wantToRequest = true;
              setTimeout(() => {
                AppointmentContext.callUpdateAppointmentCard(this.suggestionGroup.groupId, this.selectedList);
                resolve();
              });
            },
            title: 'patient.confirm',
            type: ModalButtonType.SUCCESS,
          }
        ];

        /**
         * In case of scheduling request, this modal can be opened more than once.
         * For this case, the modal must exist on screen only once.
         */
        ModalService.close('b86ad408-df95-44db-a786-eccff7b2053d');
        ModalService.openDialog({
          closeable: false,
          dialogDescription: MessageService.translate('patient.need-to-confirm'),
          dockButtons: buttons,
          id: 'b86ad408-df95-44db-a786-eccff7b2053d',
          title: 'tws.appointment-suggestion.suggestion',
        })
      }
      resolve();
    })
  }

  async availableTypeModal(suggestion: ScheduleItem): Promise<void> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<void>(async (resolve) => {
      const resolver = (): void => { return resolve(); };
      ModalService.openDefault({
        closeable: false,
        contextData: suggestion as unknown as Record<string, string>,
        dockButtons: [],
        fragment: 'suggestion-check-available',
        onClose: () => {
          resolver();
        },
        onSubmit: (event) => {
          const selectedPeriod = event.record as SuggestionPeriodItem;
          suggestion.observation = selectedPeriod.observation;
          suggestion.period = selectedPeriod.period;
          suggestion.attachedPrescheduleFiles = selectedPeriod.attachedPrescheduleFiles;
          this.selectedList.push(suggestion);
          resolver();
        },
        title: '',
      })
    })
  }

  processWeekdays(): void {
    const dateFilterItens: Record<string, Value>[] = [];
    this.suggestionList.forEach((sugestionGroup) => {
      const date = dateFilterItens.find(item => {
        return item.date === sugestionGroup.value;
      })
      if (!date) {
        dateFilterItens.push({
          date: sugestionGroup.value,
          id: sugestionGroup.value,
          value: moment(sugestionGroup.value).format('dddd,  DD/MM/YYYY') ,
        });
      }
    });
    const dateFilterItensSorted = dateFilterItens.sort((dateA,dateB) => {
      const dateAParsed = moment(dateA.date as string);
      const dateBParsed = moment(dateB.date as string);

      return dateAParsed.diff(dateBParsed);
    });

    this.segmentedControlCurrentDataToBeUsed = dateFilterItensSorted;
    if (dateFilterItensSorted.length === 1) {
      this.segmentedControlCurrentValueToBeUsed = dateFilterItensSorted;
    }
    else {
      const dateFilterItensFirstValue = [dateFilterItensSorted[0]];
      this.segmentedControlCurrentValueToBeUsed = dateFilterItensFirstValue;
    }

    if(dateFilterItensSorted.length === 0) {
      this.panelSuggestions.setVisible(true);
    }

    this.mapper0CurrentDataToBeUsed = []
  }

  @OnMount(SugestionListAliases.MAPPER_SUGGESTIONS_SUGGESTION_LIST)
  onMountMapper0(event: MountEvent): void {
    const mapperApi = event.target as MapperAPI;
    this.defineMapperDataAfterOnMount(mapperApi);
  }

  defineMapperDataAfterOnMount(mapperApi: MapperAPI): void {
    mapperApi.setData(this.mapper0CurrentDataToBeUsed);
  }

  @OnMount(SugestionListAliases.SEGMENTED_CONTROL_WEEK_DAYS)
  onMountSegmentedControlWeek(event: MountEvent): void {
    const segmmentedControlApi = event.target as SegmentedControlAPI;
    this.defineDataAndValueForSegmentedControlWeek(segmmentedControlApi);
  }

  defineDataAndValueForSegmentedControlWeek(segmentedControlApi: SegmentedControlAPI): void {
    segmentedControlApi.setData(this.segmentedControlCurrentDataToBeUsed);
    segmentedControlApi.setValue(this.segmentedControlCurrentValueToBeUsed);
  }

  @OnMount({target: 'autocomplete-establishment'})
  handleLoadEstablishments(event: MountEvent): void {
    const autocomplete = event.target as AutocompleteAPI;
    const suggestionGroupLocal = {...this.suggestionGroup};

    const establishmentsSuggestions = suggestionGroupLocal.establishmenSuggestionsGroups;

    const establishments = establishmentsSuggestions.reduce<Establishment[]>((array, establishment) => {
      array.find(item => item.id === establishment.cdEstabelecimento)
        ? array
        : array.push({
          description: establishment.dsEstablishment,
          id: establishment.cdEstabelecimento,
        });
      return array;
    }, []);

    autocomplete.setData([...establishments] as unknown as Record<string, Value>[]);
  }

  @OnMount({target: 'autocomplete-sort'})
  handleLoadAutocompleteSort(event: MountEvent): void {
    const state = this.context.get('state') as State;
    const autocomplete = event.target as AutocompleteAPI;

    const isShowDurationStr = 'S';
    const filterOrders = filterSuggestionOrders;
    const isShowDuration = isShowDurationStr === JWTTokenUtils.getClaimValue(TokenConstants.SHOW_SCHEDULING_DURATION);
    !isShowDuration && filterOrders.delete('DURATION');
    !state.isShowPrice && filterOrders.delete('AMOUNT');

    const ordinations = [...filterOrders.values()] as unknown as Record<string, Value>[];
    autocomplete.setData(ordinations);
  }

  @OnMount({target: 'filter-suggestion-times'})
  handleLoadFilterSuggestions(event: MountEvent): void {
    const filter = event.target as FilterAPI;
    filter.setData({
      ordenation: filterSuggestionOrders.get('TIME')
    });
  }

  @OnBeforeSubmit({target: 'filter-suggestion-times'})
  async handleSubmitFilterSuggestions(event: AbortableSubmitEvent): Promise<void> {
    const state = this.context.get('state') as State;
    const record = event.record as FilterSuggestionRecord;

    const { establishment, ordenation, onlyTeleconsultation } = record;
    let suggestionList = [...state.initialValues.suggestionList];
    try {
      this.panelSuggestionsContentAPI.setVisible(false);
      this.panelLoadContentAPI.setVisible(true);
      suggestionList = await new Promise<EstablishmenSuggestions[]>((resolve, reject) => {
        if(establishment) {
          suggestionList = suggestionList.filter(suggestion => suggestion.cdEstabelecimento === establishment.id)
          suggestionList = !ordenation ? this.getSuggestionsOrdered(suggestionList, 'TIME') : suggestionList;
        }

        if(onlyTeleconsultation) {
          const teleconsultationFilterResult = this.doTeleconsultationFilter(suggestionList, onlyTeleconsultation);
          suggestionList = teleconsultationFilterResult;
        }

        if(ordenation) {
          suggestionList = this.getSuggestionsOrdered(suggestionList, ordenation.id);
        }

        if(!suggestionList) {
          reject(suggestionList);
        }

        resolve(suggestionList);
      });
    } catch {
      showToastNotificationError(MessageService.translate('tws.toast-notification.load-data-error'));
    } finally {
      this.panelSuggestionsContentAPI.setVisible(true);
      this.panelLoadContentAPI.setVisible(false);
      this.suggestionList = suggestionList;
      this.processWeekdays();
    }
  }

  private doTeleconsultationFilter(suggestionList: EstablishmenSuggestions[], onlyTeleconsultation: boolean): EstablishmenSuggestions[] {
    let newSuggestionsCard = [] as EstablishmenSuggestions[];
    suggestionList.forEach(suggestionCard => {
      const suggestionGroups = suggestionCard.suggestions;
      const suggestionGroupsFiltered = this.doFilterSuggestionGroups(suggestionGroups, onlyTeleconsultation);

      if(suggestionGroupsFiltered.length > 0) {
        newSuggestionsCard = [
          ...newSuggestionsCard,
          { ...suggestionCard, suggestions: suggestionGroupsFiltered}
        ];
      }
    });

    return newSuggestionsCard;
  }

  private doFilterSuggestionGroups(suggestionGroups: SuggestionItems[], onlyTeleconsultation: boolean): SuggestionItems[] {
    let newSuggestionGroups = [] as SuggestionItems[];
    suggestionGroups.forEach(suggestionGroup => {
      const typeScheduleHybrid = 'H';
      const typeScheduleTeleconsultation = 'S';

      const newSuggestionItems = suggestionGroup.suggestionItems.filter(suggestion => suggestion.teleconsultationType === typeScheduleTeleconsultation || suggestion.teleconsultationType === typeScheduleHybrid && onlyTeleconsultation);
      if(newSuggestionItems.length > 0) {
        newSuggestionGroups = [
          ...newSuggestionGroups,
          { ...suggestionGroup }
        ];
      }
    });
    return newSuggestionGroups;
  }

  @OnBeforeClear({target: 'filter-suggestion-times'})
  handleClearButtonClick(event: AbortableSubmitEvent): void {
    const filter = event.target as FilterAPI;
    filter.clear();
    filter.setData({});
  }

  getSuggestionsOrdered(suggestions: EstablishmenSuggestions[], ordenationId: string): EstablishmenSuggestions[] {
    switch (ordenationId) {
      case 'AMOUNT': {
        return suggestions.sort((first, second) => {
          const firstValues = this.getEstablishmentValues(first.suggestions);
          const secondValues = this.getEstablishmentValues(second.suggestions);

          const firstMinValue= Math.min(...firstValues);
          const secondMinValue = Math.min(...secondValues);

          const result = this.filterSuggestionComparator(firstMinValue, secondMinValue);
          return result === 0 ? this.getResultSecondOrdenationTie(result, first, second) : result;
        })
      }
      case 'DURATION': {
        return suggestions.sort((first, second) => {
          const result = this.getSortingLogicOfTypeDuration(first, second);
          return result === 0 ? this.getResultSecondOrdenationTie(result, first, second) : result;
        });
      }
      case 'TIME': {
        return suggestions.sort((first, second) => {
          const firstStartDateTimes = this.getTimestampSuggestions(first);
          const secondStartDateTimes = this.getTimestampSuggestions(second);

          const firstMinStartDate = Math.min(...firstStartDateTimes);
          const secondMinStartDate = Math.min(...secondStartDateTimes);

          const result = this.filterSuggestionComparator(firstMinStartDate, secondMinStartDate);
          return result === 0 ? this.getSortingLogicOfTypeDuration(first, second) : result;
        });
      }
      default: {
        throw Error('ordenationId informed does not exist');
      }
    }
  }

  getSortingLogicOfTypeDuration(first: EstablishmenSuggestions, second: EstablishmenSuggestions): number {
    const firstDuration = Number(first.filterParameter?.replace(/[^0-9]/g, '') ?? 0);
    const secondDuration = Number(second.filterParameter?.replace(/[^0-9]/g, '') ?? 0);
    return this.filterSuggestionComparator(firstDuration, secondDuration);
  }

  /**
   * In case of a tie, returns the second default ordering of type 'TIME'.
   * @param currentResult Current comparison result.
   */
  getResultSecondOrdenationTie(currentResult: number,
    first: EstablishmenSuggestions, second: EstablishmenSuggestions): number {
    if(currentResult === 0) {
      const firstStartDateTimes = this.getTimestampSuggestions(first);
      const secondStartDateTimes = this.getTimestampSuggestions(second);

      const firstMinStartDate = Math.min(...firstStartDateTimes);
      const secondMinStartDate = Math.min(...secondStartDateTimes);
      return this.filterSuggestionComparator(firstMinStartDate, secondMinStartDate);
    }
    return currentResult;
  }

  filterSuggestionComparator(firstItem: number, secondItem: number): number {
    if(firstItem < secondItem) return -1;
    if(firstItem > secondItem) return 1;
    return 0;
  }

  getTimestampSuggestions(establishmentSuggestion: EstablishmenSuggestions): number[] {
    return establishmentSuggestion.events.reduce<number[]>((array, value) => {
      array.push(moment.utc(value.startDate).unix());
      return array;
    }, []);
  }

  getEstablishmentValues(suggestions: SuggestionItems[]): number[] {
    return suggestions.reduce<number[]>((result, value) => {
      const suggestItemsAmounts = value.suggestionItems.reduce<number[]>((amounts, item) => {
        amounts.push(item.priceNoFormatted);
        return amounts;
      }, []);

      result.push(Math.min(...suggestItemsAmounts));
      return result;
    }, []);
  }

  private getDesiredDate(desiredDate: MomentType, desiredTime: string): string {
    const date = moment(desiredDate).utc().format('YYYY-MM-DD');

    return date.concat('T').concat(desiredTime);
  }
}

export default SuggestionModalController;
